import React from 'react'
import {
  LeftSection,
  CardImage,
  CardImageContent,
  PopUp,
  PopUpMessage,
  PopUpButtonContainer,
  PopUpButton,
  KiroLogo,
  AgeBox,
  Text,
  SourceIdentifier,
  NameText,
  LastNameText,
  MetricRow,
  MetricButton,
  ScanBox,
  DateBox,
  NavigationButtonsContainer,
  NavigationButton,
  CheckInButton,
  DoneButton,
} from './index.styled'
import { usePatientProfile } from './usePatientProfile'
import DefaultAvatar from '../../../../images/ghostV2.svg'
import LogoBadgeOrange from '../../../../images/kiro-Logo-Orange.svg'
import LogoBadge from '../../../../images/LogoBadgeNew.png'
import LogoBadgeRed from '../../../../images/LogoBadgeRed.png'
import LogoBadgeYellow from '../../../../images/LogoBadgeYellow.png'
import { useAppSelector } from '../../../../redux/store'
import { APIStatus } from '../../../../redux/types'
import { calculateAge } from '../../../../utils/Dob'
import { formatDollars } from '../../../../utils/FormatDollars'
import { isMember } from '../../../../utils/isMember'
import { getLeadSourceLabel } from '../../../../utils/LeadSource'
import { shouldShowBday, birthdayIconColor, birthdayIconTextColor } from '../../../../utils/ShouldShowBday'

interface PatientProfilePanelProps {
  // handleForceCheckInPatient: () => void
  forceCheckInPatientStatus: APIStatus
  handlePreviousPatient: () => void
  handleNextPatient: () => void
  canGoPrevious: boolean
  canGoNext: boolean
  navigate: (path: string) => void
}

export default function PatientProfilePanel({
  // handleForceCheckInPatient,
  forceCheckInPatientStatus,
  handlePreviousPatient,
  handleNextPatient,
  canGoPrevious,
  canGoNext,
  navigate,
}: PatientProfilePanelProps) {
  const {
    currentPatient,
    formattedDate,
    isResetOtpModalOpen,
    isResetOtpLoading,
    handleProfilePicClick,
    handleResetOtpNo,
    handleResetOtpYes,
  } = usePatientProfile()

  const member = isMember(currentPatient?.userPaymentPlan?.subscriptionExpiresAt || null)
  const firstPaid = formatDollars(currentPatient?.firstPaymentAmount)

  return (
    <LeftSection>
      {currentPatient && (
        <>
          <CardImage
            $src={currentPatient?.patient?.user?.profilePic || DefaultAvatar}
            $clickable
            onClick={handleProfilePicClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleProfilePicClick()
              }
            }}
          >
            <CardImageContent>
              {(member || currentPatient?.isPaused) && (
                <KiroLogo
                  src={
                    currentPatient?.isPaused
                      ? LogoBadgeOrange
                      : currentPatient?.userPaymentPlan?.membershipStatus === 'in-active'
                        ? LogoBadgeRed
                        : LogoBadge
                  }
                  alt={currentPatient?.isPaused ? 'Membership paused' : 'Kiro'}
                />
              )}
              {currentPatient?.isPaymentPastDue && <KiroLogo src={LogoBadgeYellow} />}
              <AgeBox>
                <Text $bold>{calculateAge(currentPatient?.patient?.user?.dob)}</Text>
              </AgeBox>
            </CardImageContent>
            {/* Members already converted — the acquisition source only matters for non-members.
                A paused member is still a member even though the subscription may read expired. */}
            {!member && !currentPatient?.isPaused && (
              <SourceIdentifier $noBorder $spread>
                <Text style={{ textTransform: 'capitalize' }} $bold>
                  {getLeadSourceLabel(currentPatient?.lead)}
                </Text>
                {/* "$0" for free-flow patients who never had a payment row */}
                <Text $bold>{firstPaid}</Text>
              </SourceIdentifier>
            )}
          </CardImage>

          <NameText>{currentPatient?.patient?.user?.firstname?.toUpperCase() || ''}</NameText>
          <LastNameText>{currentPatient?.patient?.user?.lastname?.toUpperCase() || ''}</LastNameText>

          <MetricRow>
            <MetricButton
              style={{
                background:
                  !currentPatient?.feedback?.feedback && !currentPatient?.currentVisit?.isFirstBooking
                    ? '#4794EF'
                    : 'transparent',
              }}
            >
              <Text $bold $marginBottom={-8} $marginTop={-2}>
                REV
              </Text>
              <Text $size={30}>{currentPatient?.feedback?.feedback ?? '-'}</Text>
            </MetricButton>

            <MetricButton>
              <Text $bold $marginBottom={-8} $marginTop={-2}>
                REF
              </Text>
              <Text $size={30}>{currentPatient?.totalReferredPatients ?? '-'}</Text>
            </MetricButton>

            {currentPatient?.currentVisit?.isScanBooking && (
              <ScanBox>
                <Text $size={30}>SCAN</Text>
              </ScanBox>
            )}
          </MetricRow>

          {currentPatient?.patient?.user?.dob &&
          shouldShowBday(new Date(currentPatient?.patient?.user?.dob.slice(0, -1))) ? (
            <DateBox
              style={{
                background: birthdayIconColor(new Date(currentPatient?.patient?.user?.dob.slice(0, -1))) || '',
              }}
            >
              <Text
                $bold
                $color={birthdayIconTextColor(new Date(currentPatient?.patient?.user?.dob.slice(0, -1))) || ''}
              >
                {formattedDate.dayName}
              </Text>
              <Text
                $bold
                $color={birthdayIconTextColor(new Date(currentPatient?.patient?.user?.dob.slice(0, -1))) || ''}
              >
                {formattedDate.monthDay}
              </Text>
            </DateBox>
          ) : null}

          {currentPatient?.currentVisit?.status === 'booked' && (
            <NavigationButtonsContainer>
              <NavigationButton $disabled={!canGoPrevious} onClick={handlePreviousPatient} disabled={!canGoPrevious}>
                <Text $size={20} $bold $color={canGoPrevious ? 'var(--color-text-primary)' : 'var(--color-disabled)'}>
                  Previous
                </Text>
              </NavigationButton>
              <NavigationButton $disabled={!canGoNext} onClick={handleNextPatient} disabled={!canGoNext}>
                <Text $size={20} $bold $color={canGoNext ? 'var(--color-text-primary)' : 'var(--color-disabled)'}>
                  Next
                </Text>
              </NavigationButton>
            </NavigationButtonsContainer>
          )}

          {/* {currentPatient?.currentVisit?.status === 'booked' &&
            currentPatient?.patient?.isQuestionareCompleted &&
            currentPatient?.patient?.user?.profilePic &&
            currentPatient?.patient?.user?.dob &&
            currentPatient?.initialWaiver && (
              <CheckInButton
                style={{
                  background: forceCheckInPatientStatus === APIStatus.fulfilled ? '#FFFAFA' : '#007f7c',
                  borderColor: '#007f7c',
                }}
                loading={forceCheckInPatientStatus === APIStatus.pending}
                // onClick={handleForceCheckInPatient}
                type="primary"
              >
                <Text
                  $size={20}
                  $bold
                  $color={forceCheckInPatientStatus === APIStatus.fulfilled ? '#007f7c' : '#FFFAFA'}
                >
                  {forceCheckInPatientStatus === APIStatus.pending
                    ? ''
                    : forceCheckInPatientStatus === APIStatus.fulfilled
                      ? 'Checked In'
                      : 'Check In'}
                </Text>
              </CheckInButton>
            )} */}
          <DoneButton
            style={{
              marginTop: currentPatient?.currentVisit?.status === 'booked' ? '8px' : 'auto',
            }}
            onClick={() => navigate('/lobby')}
          >
            <Text $size={20} $bold $color="var(--color-text-secondary)">
              Done
            </Text>
          </DoneButton>

          <PopUp
            open={isResetOtpModalOpen}
            centered
            mask
            maskClosable={false}
            footer={null}
            closable={false}
            onCancel={handleResetOtpNo}
          >
            <PopUpMessage>Reset OTP to 123456?</PopUpMessage>
            <PopUpButtonContainer>
              <PopUpButton
                onClick={handleResetOtpNo}
                disabled={isResetOtpLoading}
                style={{ background: 'var(--color-bg)', color: 'var(--color-text-primary)' }}
              >
                No
              </PopUpButton>
              <PopUpButton
                onClick={handleResetOtpYes}
                loading={isResetOtpLoading}
                style={{ background: '#007f7c', color: 'var(--color-text-secondary)' }}
              >
                Yes
              </PopUpButton>
            </PopUpButtonContainer>
          </PopUp>
        </>
      )}
    </LeftSection>
  )
}
